import { useState } from 'react';
import { Download, Copy, Check, ChevronLeft, ChevronRight } from 'lucide-react';

interface ColumnInfo {
  name: string;
  type: string;
  displaySize: number;
  nullable: boolean;
}

interface ResultsTableProps {
  columns: ColumnInfo[];
  rows: Record<string, unknown>[];
  truncated?: boolean;
  executionTimeMs?: number;
  rowCount?: number;
}

export function ResultsTable({
  columns,
  rows,
  truncated = false,
  executionTimeMs,
  rowCount,
}: ResultsTableProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [copied, setCopied] = useState(false);
  const pageSize = 50;

  // Calculate pagination
  const totalPages = Math.ceil(rows.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, rows.length);
  const currentRows = rows.slice(startIndex, endIndex);

  // Format cell value for display
  const formatValue = (value: unknown): string => {
    if (value === null) return 'NULL';
    if (value === undefined) return '';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  // Export to CSV
  const exportToCsv = () => {
    const headers = columns.map((c) => c.name).join(',');
    const csvRows = rows.map((row) =>
      columns
        .map((col) => {
          const val = formatValue(row[col.name]);
          // Escape quotes and wrap in quotes if contains comma or newline
          if (val.includes(',') || val.includes('\n') || val.includes('"')) {
            return `"${val.replace(/"/g, '""')}"`;
          }
          return val;
        })
        .join(',')
    );
    const csv = [headers, ...csvRows].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `query_results_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Export to JSON
  const exportToJson = () => {
    const json = JSON.stringify(rows, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `query_results_${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Copy to clipboard
  const copyToClipboard = async () => {
    const headers = columns.map((c) => c.name).join('\t');
    const text = rows.map((row) =>
      columns.map((col) => formatValue(row[col.name])).join('\t')
    );
    await navigator.clipboard.writeText([headers, ...text].join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (rows.length === 0) {
    return (
      <div className="p-8 text-center text-slate-400">
        Query executed successfully. No rows returned.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Toolbar - fixed at top */}
      <div className="flex-shrink-0 flex items-center justify-between px-2 pb-3">
        <div className="text-sm text-slate-400">
          {rowCount ?? rows.length} row{(rowCount ?? rows.length) !== 1 ? 's' : ''}
          {truncated && <span className="text-amber-400 ml-2">(truncated)</span>}
          {executionTimeMs !== undefined && (
            <span className="ml-2 text-slate-500">• {executionTimeMs}ms</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={copyToClipboard}
            className="inline-flex items-center gap-1.5 px-2 py-1 text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 rounded transition-colors"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copied!' : 'Copy'}
          </button>
          <button
            onClick={exportToCsv}
            className="inline-flex items-center gap-1.5 px-2 py-1 text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 rounded transition-colors"
          >
            <Download className="h-3.5 w-3.5" />
            CSV
          </button>
          <button
            onClick={exportToJson}
            className="inline-flex items-center gap-1.5 px-2 py-1 text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 rounded transition-colors"
          >
            <Download className="h-3.5 w-3.5" />
            JSON
          </button>
        </div>
      </div>

      {/* Table - scrollable area with sticky header */}
      <div className="flex-1 min-h-0 overflow-auto rounded-lg border border-slate-700">
        <table className="w-full text-sm">
          <thead className="bg-slate-800 sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 w-12 bg-slate-800">
                #
              </th>
              {columns.map((col) => (
                <th
                  key={col.name}
                  className="px-3 py-2 text-left text-xs font-medium text-slate-400 bg-slate-800"
                >
                  <div>{col.name}</div>
                  <div className="font-normal text-slate-600">{col.type}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {currentRows.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-slate-800/30">
                <td className="px-3 py-2 text-slate-600 font-mono text-xs">
                  {startIndex + rowIndex + 1}
                </td>
                {columns.map((col) => {
                  const value = row[col.name];
                  const displayValue = formatValue(value);
                  const isNull = value === null;

                  return (
                    <td
                      key={col.name}
                      className={`px-3 py-2 font-mono text-xs max-w-xs truncate ${
                        isNull ? 'text-slate-600 italic' : 'text-slate-300'
                      }`}
                      title={displayValue}
                    >
                      {displayValue}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination - fixed at bottom */}
      {totalPages > 1 && (
        <div className="flex-shrink-0 flex items-center justify-between px-2 pt-3">
          <div className="text-xs text-slate-500">
            Showing {startIndex + 1} - {endIndex} of {rows.length}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-1 rounded hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4 text-slate-400" />
            </button>
            <span className="px-3 py-1 text-xs text-slate-400">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-1 rounded hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-4 w-4 text-slate-400" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
